import messageCircle2Icon from "@tabler/icons/outline/message-circle-2.svg";
import quoteIcon from "@tabler/icons/outline/quote.svg";
import repeatIcon from "@tabler/icons/outline/repeat.svg";
import uploadIcon from "@tabler/icons/outline/upload.svg";
import usersIcon from "@tabler/icons/outline/users.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import DropdownMenu from "@/components/dropdown-menu";
import StatusActionGroup from "@/components/statuses/status-action-group";
import { HStack, IconButton } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useComposer } from "@/contexts/composer-context";
import { useModalContext } from "@/contexts/modal-context";
import { useStatusActions } from "@/hooks";
import { type PredictionStatus } from "@/schemas";

const messages = defineMessages({
  share: { id: "status.share", defaultMessage: "Share" },
  addToWatchlist: {
    id: "predictions.addToWatchlist",
    defaultMessage: "Add to watchlist",
  },
  cancel_reblog_private: {
    id: "status.cancel_reblog_private",
    defaultMessage: "Un-ReTruth",
  },
  reblog: { id: "status.reblog", defaultMessage: "ReTruth" },
  quotePost: { id: "status.quote", defaultMessage: "Quote Truth" },
});

type PredictionActionBarLayout = "prediction" | "details";

interface PredictionActionBarProps {
  status: PredictionStatus;
  layout?: PredictionActionBarLayout;
}

export default function PredictionActionBar({
  status,
  layout = "prediction",
}: PredictionActionBarProps) {
  const isDetailLayout = layout === "details";
  const navigate = useNavigate();
  const intl = useIntl();
  const { currentAccount } = useAuth();
  const { openModal } = useModalContext();
  const { handleShare, handleReblog, handleQuote } = useStatusActions({
    status,
  });
  const composeId = status.id;
  const { updateComposer } = useComposer(composeId);

  function handleReply() {
    if (currentAccount) {
      updateComposer({
        groupId: status.group?.id,
        inReplyToStatusId: status.id,
        groupTimelineVisible: true,
      });

      openModal("COMPOSE", {
        composeId: composeId,
        groupId: status.group?.id,
        isPredictions: true,
      });
    } else {
      openModal("UNAUTHORIZED", { source: "reply" });
    }
  }

  const reblogMenu = [
    {
      text: intl.formatMessage(
        status.reblogged ? messages.cancel_reblog_private : messages.reblog,
      ),
      action: handleReblog,
      icon: repeatIcon,
    },
    {
      text: intl.formatMessage(messages.quotePost),
      action: handleQuote,
      icon: quoteIcon,
    },
  ];

  const reblogButton = (
    <StatusActionGroup
      layout={layout}
      primaryAction={{
        count: status.reblogs_count,
        handler: (e) => {
          e.stopPropagation();
          e.preventDefault();
          handleReblog();
        },
        iconSrc: repeatIcon,
        color: "success",
        active: status.reblogged,
        "aria-label": intl.formatMessage(messages.reblog),
      }}
    />
  );

  const canShare = "share" in navigator;

  const replyButton = (
    <StatusActionGroup
      layout={layout}
      primaryAction={{
        count: status.replies_count,
        handler: (e) => {
          e.stopPropagation();
          e.preventDefault();
          handleReply();
        },
        iconSrc: messageCircle2Icon,
        color: "primary",
        "aria-label": "Reply",
      }}
    />
  );

  const reblogControl = currentAccount ? (
    <DropdownMenu
      items={reblogMenu}
      onShiftClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleReblog();
      }}
      width="normal"
    >
      {reblogButton}
    </DropdownMenu>
  ) : (
    reblogButton
  );

  const shareButton = canShare && (
    <StatusActionGroup
      primaryAction={{
        "aria-label": intl.formatMessage(messages.share),
        iconSrc: uploadIcon,
        handler: (e) => {
          e.stopPropagation();
          e.preventDefault();
          handleShare();
        },
        color: "default",
      }}
      layout={layout}
    />
  );

  if (isDetailLayout) {
    return (
      <HStack
        space={3}
        justifyContent="between"
        alignItems="stretch"
        grow
        onClick={(e) => e.stopPropagation()}
        className="cursor-default *:w-full"
        data-testid="status-action-bar"
      >
        {replyButton}
        {reblogControl}
        {shareButton}
      </HStack>
    );
  }

  return (
    <HStack space={3} justifyContent="between" alignItems="center">
      <HStack
        space={9}
        onClick={(e) => e.stopPropagation()}
        className="cursor-default"
        data-testid="status-action-bar"
      >
        {replyButton}
        {reblogControl}
        {shareButton}
      </HStack>
      <IconButton
        src={usersIcon}
        iconClassName="size-5"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigate(`/group/${status.group.slug}`);
        }}
      />
    </HStack>
  );
}
